// apps/hyperseguros-web/src/components/Nav/MobileMenu.tsx

'use client'

import React, { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Box, useTheme } from '@mui/material'
import { UIButton } from 'renaissance/components/UIButton'
import { ctaAccentSx } from './ctaAccent'
import type { TNavConfig, TNavItem, TNavMenu, TNavMenuGroup } from './types'

export const MOBILE_ROOT_VIEW_ID = 'nav-mobile-root'
export const MOBILE_MENU_VIEW_ID = 'nav-mobile-menu'
export const MOBILE_GROUP_VIEW_ID = 'nav-mobile-group'

type Props = {
  open: boolean
  /** `[menuLabel]` or `[menuLabel, groupHeading]`; empty at the root. */
  stack: string[]
  config: TNavConfig
  onPush: (id: string) => void
  onNavigate: () => void
}

const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)'
const EASE_STD = 'cubic-bezier(0.4, 0, 0.6, 1)'
/** UIKit's navigation push curve. */
const EASE_IOS = 'cubic-bezier(0.32, 0.72, 0, 1)'
const PUSH_DURATION = '420ms'
/** The outgoing view fades faster than it slides so the two never ghost. */
const FADE_DURATION = '240ms'
/** Delay before the first row starts, so the card has begun to grow. */
const STAGGER_BASE_MS = 90
/** Extra delay per row. */
const STAGGER_STEP_MS = 35
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)'
/** 9px left so the rows start where the trigger's lines do. */
const VIEW_PADDING = '4px 4px 20px 9px'

/**
 * Body of the expanded mobile nav. It is the content of the pill's panel
 * row (see NavBar), so it stays mounted while closed: the root rows fade in
 * with a stagger when `open` flips and fade out together when it clears,
 * like apple.com's flyout.
 *
 * Screens form an iOS navigation stack: root list → a menu's screen (its
 * headed groups as rows, a heading-less group's links inline) → a group's
 * screen (its links). Each push slides the new screen in from the right
 * while the one beneath parallaxes a third of the way left and fades; the
 * back arrow in the bar (NavBar's trigger) pops one level and the motion
 * reverses. Each screen scrolls on its own.
 */
export default function MobileMenu({
  open,
  stack,
  config,
  onPush,
  onNavigate,
}: Props) {
  const theme = useTheme()
  const hairline =
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)'

  const [menuLabel, groupHeading] = stack
  const menu = menuLabel
    ? (config.items.find(
        (i): i is TNavMenu => i.type === 'menu' && i.label === menuLabel
      ) ?? null)
    : null
  const group =
    menu && groupHeading
      ? (menu.groups.find((g) => g.heading === groupHeading) ?? null)
      : null
  const depth = group ? 2 : menu ? 1 : 0

  // Keep a popped screen rendered while it slides out, then drop it once
  // the slide has ended so the closed menu holds no stale content.
  const keptMenu = useKept(menu)
  const keptGroup = useKept(group)

  const viewBase = {
    position: 'absolute',
    inset: 0,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
    padding: VIEW_PADDING,
    transition: `transform ${PUSH_DURATION} ${EASE_IOS}, opacity ${FADE_DURATION} ${EASE_STD}`,
    [REDUCED_MOTION]: { transition: 'none' },
  } as const

  // A screen sits at 0 when it is the top of the stack, a third of the way
  // left (and faded) once something is pushed over it, and off to the right
  // until it is pushed.
  const placement = (level: number) =>
    depth === level
      ? { transform: 'translateX(0)', opacity: 1 }
      : depth > level
        ? { transform: 'translateX(-33%)', opacity: 0 }
        : { transform: 'translateX(100%)', opacity: 1 }

  return (
    <>
      <Box
        id={MOBILE_ROOT_VIEW_ID}
        component="ul"
        aria-hidden={depth !== 0}
        inert={depth !== 0 ? true : undefined}
        sx={{ ...viewBase, listStyle: 'none', margin: 0, ...placement(0) }}
      >
        {config.items.map((item, index) => (
          <Box
            component="li"
            key={`${item.type}-${item.label}`}
            sx={{
              opacity: open ? 1 : 0,
              transform: open ? 'translateY(0)' : 'translateY(-10px)',
              transition: open
                ? `opacity 380ms ${EASE_OUT} ${STAGGER_BASE_MS + index * STAGGER_STEP_MS}ms, transform 480ms ${EASE_OUT} ${STAGGER_BASE_MS + index * STAGGER_STEP_MS}ms`
                : `opacity 160ms ${EASE_STD}, transform 160ms ${EASE_STD}`,
              [REDUCED_MOTION]: { transition: 'none', transform: 'none' },
              borderBottom:
                item.type === 'cta' ? 'none' : `1px solid ${hairline}`,
            }}
          >
            <RootRow item={item} onPush={onPush} onNavigate={onNavigate} />
          </Box>
        ))}
      </Box>

      <Screen
        id={MOBILE_MENU_VIEW_ID}
        label={keptMenu.value?.label}
        active={depth === 1}
        onSlideEnd={keptMenu.release}
        sx={{ ...viewBase, ...placement(1) }}
      >
        {keptMenu.value && (
          <MenuScreen
            menu={keptMenu.value}
            hairline={hairline}
            onPush={onPush}
            onNavigate={onNavigate}
          />
        )}
      </Screen>

      <Screen
        id={MOBILE_GROUP_VIEW_ID}
        label={keptGroup.value?.heading}
        active={depth === 2}
        onSlideEnd={keptGroup.release}
        sx={{ ...viewBase, ...placement(2) }}
      >
        {keptGroup.value && (
          <GroupScreen
            group={keptGroup.value}
            parentLabel={keptMenu.value?.label}
            hairline={hairline}
            onNavigate={onNavigate}
          />
        )}
      </Screen>
    </>
  )
}

/**
 * Holds on to the last non-null value until `release` is called (from the
 * slide-out's transitionend), so a popped screen keeps its content while
 * it animates away.
 */
function useKept<T>(value: T | null) {
  const [kept, setKept] = useState<T | null>(null)
  useEffect(() => {
    if (value) setKept(value)
  }, [value])
  const release = () => {
    if (!value) setKept(null)
  }
  return { value: value ?? kept, release }
}

function Screen({
  id,
  label,
  active,
  onSlideEnd,
  sx,
  children,
}: {
  id: string
  label: string | undefined
  active: boolean
  onSlideEnd: () => void
  sx: React.ComponentProps<typeof Box>['sx']
  children: React.ReactNode
}) {
  const ref = useRef<globalThis.HTMLDivElement | null>(null)
  // A screen always opens scrolled to the top.
  useEffect(() => {
    if (active && ref.current) ref.current.scrollTop = 0
  }, [active, label])
  return (
    <Box
      id={id}
      ref={ref}
      role="region"
      aria-label={label}
      aria-hidden={!active}
      inert={active ? undefined : true}
      onTransitionEnd={(e) => {
        if (e.propertyName === 'transform' && !active) onSlideEnd()
      }}
      sx={sx}
    >
      {children}
    </Box>
  )
}

const ROW = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  width: '100%',
  minHeight: '56px',
  padding: '14px 4px',
  fontSize: '22px',
  lineHeight: 1.2,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: 'inherit',
  textDecoration: 'none',
  textAlign: 'left',
  background: 'transparent',
  border: 'none',
  fontFamily: 'inherit',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  '&:focus-visible': {
    outline: '2px solid currentColor',
    outlineOffset: '-2px',
    borderRadius: '8px',
  },
} as const

/** Rows on the pushed screens: one step smaller and lighter than the root. */
const SUB_ROW = {
  ...ROW,
  minHeight: '52px',
  padding: '12px 4px',
  fontSize: '19px',
  fontWeight: 500,
} as const

function PushRow({ label, onPush }: { label: string; onPush: () => void }) {
  return (
    <Box component="button" type="button" onClick={onPush} sx={SUB_ROW}>
      <span>{label}</span>
      <Chevron />
    </Box>
  )
}

function LinkRow({
  label,
  href,
  onNavigate,
}: {
  label: string
  href: string
  onNavigate: () => void
}) {
  return (
    <Link href={href} onClick={onNavigate} style={{ display: 'block' }}>
      <Box sx={SUB_ROW}>{label}</Box>
    </Link>
  )
}

function RootRow({
  item,
  onPush,
  onNavigate,
}: {
  item: TNavItem
  onPush: (id: string) => void
  onNavigate: () => void
}) {
  const theme = useTheme()
  if (item.type === 'menu') {
    return (
      <Box
        component="button"
        type="button"
        onClick={() => onPush(item.label)}
        sx={ROW}
      >
        <span>{item.label}</span>
        <Chevron />
      </Box>
    )
  }
  if (item.type === 'cta') {
    // Full width so the button reads as one more row of the list.
    return (
      <Box sx={{ pt: 3, pb: 1, px: '4px' }}>
        <Link
          href={item.href}
          onClick={onNavigate}
          style={{ textDecoration: 'none', display: 'block' }}
        >
          <Box
            sx={
              item.accent
                ? ctaAccentSx(item.accent, theme.palette.mode)
                : undefined
            }
          >
            <UIButton
              label={item.label}
              variant={item.buttonVariant ?? 'contained'}
              color="primary"
              fullWidth
              onClick={() => {}}
            />
          </Box>
        </Link>
      </Box>
    )
  }
  return (
    <Link href={item.href} onClick={onNavigate} style={{ display: 'block' }}>
      <Box sx={ROW}>
        <span>{item.label}</span>
        <Chevron />
      </Box>
    </Link>
  )
}

function ScreenTitle({
  eyebrow,
  children,
}: {
  eyebrow?: string
  children: React.ReactNode
}) {
  const theme = useTheme()
  // Same muted gray as Footer/index.tsx's `mutedColor`, not an opacity on
  // the inherited (unset) text color: this card has no explicit color of
  // its own, so it inherits full-strength black/white (browsers resolve
  // canvastext off `color-scheme`), and opacity 0.55 on that only clears
  // ~4.6-4.95:1 against the mobile card's own expanded composite (its
  // 0.78-alpha white/black tint over the page background) — a margin the
  // card's real backdrop (whatever page content is behind the glass when
  // it opens) can erase. The literal clears 4.5:1 against that composite
  // in both modes (~4.95:1 light, ~4.62:1 dark) independent of the backdrop.
  const eyebrowColor = theme.palette.mode === 'dark' ? '#86868b' : '#6e6e73'
  return (
    <Box sx={{ px: '4px', pt: '10px', pb: '6px' }}>
      {eyebrow && (
        <Box
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: eyebrowColor,
            mb: 0.5,
          }}
        >
          {eyebrow}
        </Box>
      )}
      <Box
        component="h2"
        sx={{
          margin: 0,
          fontSize: '28px',
          lineHeight: 1.15,
          fontWeight: 600,
          letterSpacing: '-0.015em',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

/**
 * A menu's screen. Its headed groups are rows that push the group screen;
 * a heading-less group has nothing to name a screen after, so its links
 * sit inline, first.
 */
function MenuScreen({
  menu,
  hairline,
  onPush,
  onNavigate,
}: {
  menu: TNavMenu
  hairline: string
  onPush: (id: string) => void
  onNavigate: () => void
}) {
  const inline = menu.groups.filter((g) => !g.heading).flatMap((g) => g.items)
  const headed = menu.groups.filter(
    (g): g is TNavMenuGroup & { heading: string } => !!g.heading
  )
  return (
    <>
      <ScreenTitle>{menu.label}</ScreenTitle>
      <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {inline.map((link) => (
          <Box
            component="li"
            key={link.href}
            sx={{ borderBottom: `1px solid ${hairline}` }}
          >
            <LinkRow
              label={link.label}
              href={link.href}
              onNavigate={onNavigate}
            />
          </Box>
        ))}
        {headed.map((g) => (
          <Box
            component="li"
            key={g.heading}
            sx={{ borderBottom: `1px solid ${hairline}` }}
          >
            <PushRow label={g.heading} onPush={() => onPush(g.heading)} />
          </Box>
        ))}
      </Box>
    </>
  )
}

/** A group's screen: the menu it belongs to as an eyebrow, then its links. */
function GroupScreen({
  group,
  parentLabel,
  hairline,
  onNavigate,
}: {
  group: TNavMenuGroup
  parentLabel: string | undefined
  hairline: string
  onNavigate: () => void
}) {
  return (
    <>
      <ScreenTitle eyebrow={parentLabel}>{group.heading}</ScreenTitle>
      <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {group.items.map((link) => (
          <Box
            component="li"
            key={link.href}
            sx={{ borderBottom: `1px solid ${hairline}` }}
          >
            <LinkRow
              label={link.label}
              href={link.href}
              onNavigate={onNavigate}
            />
          </Box>
        ))}
      </Box>
    </>
  )
}

/** Inline SVG so the site needs no icon font (see the root layout). */
function Chevron() {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      width={26}
      height={26}
      aria-hidden="true"
      sx={{ flex: 'none', opacity: 0.45, display: 'block' }}
    >
      <path
        d="M9 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  )
}
